import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MsalService } from '@azure/msal-angular';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { firstValueFrom, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { QuillModule } from 'ngx-quill';
import { NgxDropzoneModule } from 'ngx-dropzone';
import { getAllMeetingTypes$Json } from '../../api/fn/meeting-type/get-all-meeting-types-json';
import { getAllMeetingLocations$Json } from '../../api/fn/meeting-location/get-all-meeting-locations-json';
import { getTemplateById$Json } from '../../api/fn/template/get-template-by-id-json';
import { getCases$Json } from '../../api/fn/case/get-cases-json';
import { getContactsByCase$Json } from '../../api/fn/contact/get-contacts-by-case-json';
import { ApiConfiguration } from '../../api/api-configuration';
import { SelectionResponse } from 'src/app/api/models/selection-response';
import { CalendarInfo } from 'src/app/api/models/calendar-info';
import { MeetingType } from 'src/app/api/models/meeting-type';
import { Case } from 'src/app/api/models/case';
import { Contact } from 'src/app/api/models/contact';
import { MeetingLocation } from 'src/app/api/models/meeting-location';
import { getUserCalendarDelegates$Json } from 'src/app/api/functions';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-user-selection',
    imports: [
        CommonModule,
        FormsModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatSnackBarModule,
        MatAutocompleteModule,
        QuillModule,
        NgxDropzoneModule
    ],
    templateUrl: './user-selection.component.html',
    styleUrls: ['./user-selection.component.scss']
})
export class UserSelectionComponent implements OnInit {
  calendars: CalendarInfo[] = [];
  selectedCalendar: CalendarInfo | null = null;
  isLoadingCalendars = false;
  meetingTypes: MeetingType[] = [];
  selectedMeetingType: MeetingType | null = null;
  isLoadingMeetingTypes = false;
  allMeetingLocations: MeetingLocation[] = [];
  filteredMeetingLocations: MeetingLocation[] = [];
  selectedMeetingLocation: MeetingLocation | null = null;
  isLoadingMeetingLocations = false;
  contacts: Contact[] = [];
  selectedContact: Contact | null = null;
  selectedAttendees: Contact[] = [];
  isLoadingContacts = false;
  emailContent: string = '';
  isLoadingTemplate = false;
  isSending = false;

  // File attachments (ngx-dropzone)
  uploadedFiles: File[] = [];
  readonly acceptedFileTypes = '.pdf,.doc,.docx';
  private static readonly MAX_FILE_BYTES = 25 * 1024 * 1024; // 25 MB per file
  private static readonly MAX_FILE_COUNT = 10;
  private static readonly ALLOWED_EXTENSIONS = ['pdf', 'doc', 'docx'];

  // Case search fields
  caseSearchText: string = '';
  caseSearchResults: Case[] = [];
  selectedCase: Case | null = null;
  isSearchingCases = false;
  private caseSearchSubject = new Subject<string>();

  // Quill editor configuration
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      ['clean']
    ],
    clipboard: {
      matchers: [
        ['img', (_node: any, _delta: any) => ({ ops: [] })]
      ]
    }
  };

  constructor(
    private http: HttpClient,
    private apiConfig: ApiConfiguration,
    private snackBar: MatSnackBar,
    private authService: MsalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadCalendars();
    this.loadMeetingTypes();
    this.loadMeetingLocations();
    this.setupCaseSearch();
  }

  /**
   * Load calendars from the API
   */
  async loadCalendars(): Promise<void> {
    this.isLoadingCalendars = true;

    try {
      const response = await firstValueFrom(
        getUserCalendarDelegates$Json(this.http, this.apiConfig.rootUrl)
      );

      this.calendars = response.body || [];

      // Auto-select the user's own calendar if available
      const ownCalendar = this.calendars.find(c => c.isOwn);
      if (ownCalendar) {
        this.selectedCalendar = ownCalendar;
      } else if (this.calendars.length > 0) {
        this.selectedCalendar = this.calendars[0];
      }

      this.isLoadingCalendars = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isLoadingCalendars = false;
      this.cdr.detectChanges();
      this.showError(`Failed to load calendars: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Load meeting types from the API
   */
  async loadMeetingTypes(): Promise<void> {
    this.isLoadingMeetingTypes = true;

    try {
      const response = await firstValueFrom(
        getAllMeetingTypes$Json(this.http, this.apiConfig.rootUrl)
      );

      this.meetingTypes = response.body || [];

      // Auto-select the first meeting type if available
      if (this.meetingTypes.length > 0) {
        this.selectedMeetingType = this.meetingTypes[0];
        this.onMeetingTypeChange();
      }

      this.isLoadingMeetingTypes = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isLoadingMeetingTypes = false;
      this.cdr.detectChanges();
      this.showError(`Failed to load meeting types: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Load meeting locations from the API
   */
  async loadMeetingLocations(): Promise<void> {
    this.isLoadingMeetingLocations = true;

    try {
      const response = await firstValueFrom(
        getAllMeetingLocations$Json(this.http, this.apiConfig.rootUrl)
      );

      this.allMeetingLocations = response.body || [];
      this.onMeetingTypeChange();

      this.isLoadingMeetingLocations = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isLoadingMeetingLocations = false;
      this.cdr.detectChanges();
      this.showError(`Failed to load meeting locations: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Handle meeting type selection change - filter locations based on type
   */
  onMeetingTypeChange(): void {
    if (!this.allMeetingLocations.length) {
      return;
    }

    const typeName = this.selectedMeetingType?.name?.toLowerCase() ?? '';
    const isPhoneCall = typeName.includes('phone');
    const isInPersonOnly = typeName.includes('signing') || typeName.includes('design');

    if (isPhoneCall) {
      // For phone calls, only show the Phone location
      this.filteredMeetingLocations = this.allMeetingLocations.filter(
        loc => loc.name?.toLowerCase() === 'phone'
      );
    } else if (isInPersonOnly) {
      // For signing/design, hide the Phone location
      this.filteredMeetingLocations = this.allMeetingLocations.filter(
        loc => loc.name?.toLowerCase() !== 'phone'
      );
    } else {
      // For other meeting types, show all locations
      this.filteredMeetingLocations = [...this.allMeetingLocations];
    }

    // Auto-select the first available location, or the only one if phone
    if (this.filteredMeetingLocations.length > 0) {
      this.selectedMeetingLocation = this.filteredMeetingLocations[0];
      this.loadEmailTemplate();
    } else {
      this.selectedMeetingLocation = null;
    }

    // Location is currently hardcoded to In-Person (the location form field is hidden).
    // Remove this override when the location form field is re-enabled.
    this.selectedMeetingLocation = this.allMeetingLocations.find(
      loc => loc.name?.toLowerCase() === 'in-person'
    ) ?? this.selectedMeetingLocation;

    // Drop any staged files when switching to a meeting type that has no attachments.
    if (!this.showAttachments) {
      this.uploadedFiles = [];
    }
  }

  /**
   * Load email template from the API based on selected location
   */
  async loadEmailTemplate(): Promise<void> {
    if (!this.selectedMeetingType?.meetingTypeId) {
      return;
    }

    this.isLoadingTemplate = true;

    try {
      const meetingTypeId = this.selectedMeetingType.meetingTypeId.toString();
      const response = await firstValueFrom(
        getTemplateById$Json(this.http, this.apiConfig.rootUrl, { name: 'ShareLinkTemplate', id: meetingTypeId })
      );

      this.emailContent = response.body?.body || '';
      this.isLoadingTemplate = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isLoadingTemplate = false;
      this.cdr.detectChanges();
      this.showError(`Failed to load email template: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Set up the case search with debouncing
   */
  private setupCaseSearch(): void {
    const minChars = environment.caseSearchMinChars;
    this.caseSearchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(searchText => {
        if (!searchText || searchText.length < minChars) {
          this.caseSearchResults = [];
          this.isSearchingCases = false;
          return [];
        }
        this.isSearchingCases = true;
        return getCases$Json(this.http, this.apiConfig.rootUrl, { searchString: searchText });
      })
    ).subscribe({
      next: (response) => {
        if (response) {
          this.caseSearchResults = response.body || [];
        }
        this.isSearchingCases = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.isSearchingCases = false;
        this.caseSearchResults = [];
        this.showError(`Failed to search cases: ${error.message || 'Unknown error'}`);
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * Handle case search input changes
   */
  onCaseSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.caseSearchSubject.next(input.value);
  }

  /**
   * Handle case selection from autocomplete
   */
  onCaseSelected(selectedCase: Case): void {
    this.selectedCase = selectedCase;
    this.caseSearchText = this.getCaseDisplayName(selectedCase);
    this.loadContactsByCase(selectedCase.caseID!);
  }

  /**
   * Load contacts for the selected case
   */
  async loadContactsByCase(caseId: number): Promise<void> {
    this.isLoadingContacts = true;
    this.contacts = [];
    this.selectedContact = null;
    this.selectedAttendees = [];

    try {
      const response = await firstValueFrom(
        getContactsByCase$Json(this.http, this.apiConfig.rootUrl, { caseId })
      );

      this.contacts = response.body || [];
      this.isLoadingContacts = false;
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isLoadingContacts = false;
      this.cdr.detectChanges();
      this.showError(`Failed to load contacts: ${error.message || 'Unknown error'}`);
    }
  }

  /**
   * Contacts available as the recipient — anyone already chosen as an attendee is excluded.
   */
  get availableRecipients(): Contact[] {
    return this.contacts.filter(c => !this.selectedAttendees.includes(c));
  }

  /**
   * Contacts available as attendees — the chosen recipient is excluded.
   */
  get availableAttendees(): Contact[] {
    return this.contacts.filter(c => c !== this.selectedContact);
  }

  /**
   * Drop the newly selected recipient from the attendee list so a contact can
   * never occupy both roles.
   */
  onRecipientChange(): void {
    if (this.selectedContact) {
      this.selectedAttendees = this.selectedAttendees.filter(c => c !== this.selectedContact);
    }
  }

  /**
   * Get display name for a contact
   */
  getContactDisplayName(contact: Contact): string {
    const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
    if (contact.contactEmail) {
      return `${name} (${contact.contactEmail})`;
    }
    return name;
  }

  /**
   * Get display name for a case
   */
  getCaseDisplayName(caseItem: Case): string {
    return `${caseItem.caseNumber} - ${caseItem.caseName}`;
  }

  /**
   * Clear the selected case
   */
  clearCase(): void {
    this.selectedCase = null;
    this.caseSearchText = '';
    this.caseSearchResults = [];
    this.contacts = [];
    this.selectedContact = null;
    this.selectedAttendees = [];
    this.caseSearchSubject.next('');
  }

  /**
   * Get display name for a meeting type (includes duration)
   */
  getMeetingTypeDisplayName(meetingType: MeetingType): string {
    return `${meetingType.name} (${meetingType.minuteLength} min)`;
  }

  /**
   * Get display name for a calendar
   */
  getCalendarDisplayName(calendar: CalendarInfo): string {
    if (calendar.isOwn) {
      return `${calendar.ownerName} (You)`;
    }
    return `${calendar.ownerName} - ${calendar.name}`;
  }

  /**
   * Select a calendar from the list
   */
  selectCalendar(calendar: CalendarInfo): void {
    this.selectedCalendar = calendar;
  }

  /**
   * Block image insertion in the Quill editor
   */
  onEditorCreated(editor: any): void {
    editor.getModule('toolbar')?.addHandler('image', () => {});
    editor.root.addEventListener('drop', (e: DragEvent) => {
      const hasFiles = e.dataTransfer?.types?.includes('Files');
      if (hasFiles) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  /**
   * Attachments only apply to signing meetings ("Signing" and "Design and Signing").
   */
  get showAttachments(): boolean {
    return this.selectedMeetingType?.name?.toLowerCase().includes('signing') ?? false;
  }

  /**
   * Handle files added via the dropzone. Validates type, size, and count,
   * surfacing a message for any rejected file.
   */
  onFilesAdded(event: { addedFiles: File[]; rejectedFiles: File[] }): void {
    for (const rejected of event.rejectedFiles) {
      this.showError(`"${rejected.name}" is not an allowed type. Only PDF and Word documents are accepted.`);
    }

    for (const file of event.addedFiles) {
      const ext = file.name.split('.').pop()?.toLowerCase() ?? '';

      if (!UserSelectionComponent.ALLOWED_EXTENSIONS.includes(ext)) {
        this.showError(`"${file.name}" is not an allowed type. Only PDF and Word documents are accepted.`);
        continue;
      }
      if (file.size > UserSelectionComponent.MAX_FILE_BYTES) {
        this.showError(`"${file.name}" exceeds the 25 MB per-file limit.`);
        continue;
      }
      if (this.uploadedFiles.length >= UserSelectionComponent.MAX_FILE_COUNT) {
        this.showError(`You can attach at most ${UserSelectionComponent.MAX_FILE_COUNT} files.`);
        break;
      }
      this.uploadedFiles.push(file);
    }
  }

  /**
   * Remove a previously added file from the upload list.
   */
  onFileRemoved(file: File): void {
    this.uploadedFiles = this.uploadedFiles.filter(f => f !== file);
  }

  /**
   * Human-readable file size for display.
   */
  formatFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  /**
   * Check if form is valid
   */
  isFormValid(): boolean {
    return this.selectedCalendar !== null &&
           this.selectedMeetingType !== null &&
           this.selectedMeetingLocation !== null &&
           this.selectedCase !== null &&
           this.selectedContact !== null &&
           this.emailContent !== '' &&
           !this.isSending &&
           !this.isLoadingCalendars &&
           !this.isLoadingMeetingTypes &&
           !this.isLoadingMeetingLocations &&
           !this.isLoadingContacts &&
           !this.isLoadingTemplate;
  }

  /**
   * Send selection to API
   */
  async sendSelection(): Promise<void> {
    if (!this.isFormValid()) {
      return;
    }

    this.isSending = true;

    // Build multipart/form-data so uploaded files are sent alongside the form fields.
    // Field names match the SelectionRequest properties bound on the API ([FromForm]).
    const formData = new FormData();
    formData.append('user1Id', this.selectedCalendar?.ownerEmail || '');
    formData.append('user2Id', this.selectedContact?.contactEmail || '');
    formData.append('user2ContactId', this.selectedContact?.contactId?.toString() || '');
    // Attendee contact ids are posted as repeated fields; the API joins them into the
    // comma separated Attendees column on the validation key.
    for (const attendee of this.selectedAttendees) {
      if (attendee.contactId != null) {
        formData.append('attendeeContactIds', attendee.contactId.toString());
      }
    }
    formData.append('meetingTypeId', (this.selectedMeetingType?.meetingTypeId || 0).toString());
    formData.append('meetingLocationId', (this.selectedMeetingLocation?.meetingLocationId || 0).toString());
    formData.append('emailContent', this.emailContent);
    formData.append('caseId', (this.selectedCase?.caseID ?? 0).toString());
    for (const file of this.uploadedFiles) {
      formData.append('files', file, file.name);
    }

    try {
      // Use HttpClient directly (the generated client is JSON-only). The MSAL
      // interceptor attaches the bearer token by URL, and the browser sets the
      // multipart Content-Type with boundary automatically for FormData bodies.
      const response = await firstValueFrom(
        this.http.post<SelectionResponse>(
          `${this.apiConfig.rootUrl}/api/Selection`,
          formData,
          { observe: 'response' }
        )
      ) as HttpResponse<SelectionResponse>;

      this.isSending = false;
      const data = response.body;

      if (data?.success) {
        const contactName = [this.selectedContact?.firstName, this.selectedContact?.lastName].filter(Boolean).join(' ');
        this.showSuccess(`Calendar link sent successfully to ${contactName}`);
        // Reset inputs after successful submission
        this.uploadedFiles = [];
        this.clearCase();
      } else {
        this.showError(data?.message || 'Failed to send calendar link');
      }
      this.cdr.detectChanges();
    } catch (error: any) {
      this.isSending = false;
      this.cdr.detectChanges();
      this.showError(`Failed to send selection: ${error.error?.message || error.message || 'Unknown error'}`);
    }
  }

  /**
   * Show success message
   */
  private showSuccess(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['success-snackbar']
    });
  }

  /**
   * Show error message
   */
  private showError(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
      panelClass: ['error-snackbar']
    });
  }
}
